import { getErrorMessage } from "@/utils/errorHelpers";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { getFileUrl, productsAPI } from "@/services/api";
import { toast } from "sonner";
import { PRODUCT_TYPES, PRODUCT_STATUS, formatCurrency, formatDate } from "@/utils/constants";
import { getProductCategoryLabel, getProductSubcategories, PRODUCT_CATEGORIES } from "@/utils/productCategories";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, XCircle, Eye, Download, FileText } from "lucide-react";
import { CardGrid, LoadingState, PageHeader, PageShell } from "@/components/app/page";

export default function ProductsManagementPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [validating, setValidating] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [coverFile, setCoverFile] = useState(null);
  const [productFile, setProductFile] = useState(null);
  const [validationData, setValidationData] = useState({
    status: "validated",
    rejection_reason: "",
  });

  const buildEditData = (product) => ({
    title: product?.title || "",
    description: product?.description || "",
    author_name: product?.author_name || "",
    product_type: product?.product_type || "texte_simple",
    category: product?.category || "",
    subcategory: product?.subcategory || "",
    price_mc: product?.price_mc ?? 0,
    summary: product?.summary || "",
    author_about: product?.author_about || "",
    is_free_for_active_par: !!product?.is_free_for_active_par,
    free_access_start: product?.free_access_start || "",
    free_access_end: product?.free_access_end || "",
    notes: "",
  });
  const [editData, setEditData] = useState(buildEditData(null));

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await productsAPI.list();
      setProducts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error("Erreur lors du chargement des produits");
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setEditData(buildEditData(product));
    setCoverFile(null);
    setProductFile(null);
  };

  const handleEditProduct = async (e) => {
    e.preventDefault();
    if (!editingProduct) return;
    setSavingEdit(true);
    try {
      await productsAPI.update(editingProduct.id, {
        ...editData,
        price_mc: Number(editData.price_mc || 0),
        free_access_start: editData.free_access_start || null,
        free_access_end: editData.free_access_end || null,
      });
      if (coverFile) await productsAPI.uploadCover(editingProduct.id, coverFile);
      if (productFile) await productsAPI.uploadFile(editingProduct.id, productFile);
      toast.success(editData.is_free_for_active_par ? "Produit mis a jour. L'accès libre attend l'approbation FULL." : "Produit mis a jour.");
      setEditingProduct(null);
      loadProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur modification produit"));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleValidate = async (e) => {
    e.preventDefault();
    if (!selectedProduct) return;

    setValidating(true);
    try {
      await productsAPI.validate(selectedProduct.id, validationData);
      toast.success(
        validationData.status === "validated" ? "Produit validé avec succès !" : "Produit rejeté"
      );
      setSelectedProduct(null);
      setValidationData({ status: "validated", rejection_reason: "" });
      loadProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de la validation"));
    } finally {
      setValidating(false);
    }
  };

  const safeProducts = Array.isArray(products) ? products : [];
  const pendingProducts = safeProducts.filter((p) => p.status === "pending");
  const validatedProducts = safeProducts.filter((p) => p.status === "validated");
  const rejectedProducts = safeProducts.filter((p) => p.status === "rejected");

  if (loading) {
    return <LoadingState label="Chargement des produits..." />;
  }

  return (
    <PageShell data-testid="products-management-page">
      <PageHeader title="Gestion des produits" />

      <CardGrid className="mb-8 lg:grid-cols-3">
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm font-medium text-[#5C625F]">En attente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-600" data-testid="pending-count">
              {pendingProducts.length}
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm font-medium text-[#5C625F]">Validés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600" data-testid="validated-count">
              {validatedProducts.length}
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm font-medium text-[#5C625F]">Rejetés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600" data-testid="rejected-count">
              {rejectedProducts.length}
            </div>
          </CardContent>
        </Card>
      </CardGrid>

      {/* Products List */}
      <Card className="border-[#E6E4E0] shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">
            Tous les produits ({safeProducts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {safeProducts.length === 0 ? (
            <p className="text-center text-[#5C625F] py-8">Aucun produit soumis</p>
          ) : (
            <div className="space-y-4">
              {safeProducts.map((product) => (
                <div
                  key={product.id}
                  className="rounded-2xl border border-[#E6E4E0] p-4 transition-shadow hover:shadow-md"
                  data-testid={`product-item-${product.id}`}
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex-1">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <h3 className="text-lg font-semibold text-[#2A4B46]">{product.title}</h3>
                        <Badge className={PRODUCT_STATUS[product.status]?.color}>
                          {PRODUCT_STATUS[product.status]?.label}
                        </Badge>
                        <Badge className="bg-[#F3EFE9] text-[#2A4B46]">
                          {PRODUCT_TYPES[product.product_type]?.label}
                        </Badge>
                        {product.institutional_catalogue && (
                          <Badge className="bg-[#E7EFEC] text-[#2A4B46]">Catalogue institutionnel</Badge>
                        )}
                      </div>
                      <p className="text-sm text-[#5C625F] mb-2">{product.description || "Aucune description fournie."}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-[#5C625F] sm:gap-4">
                        <span>Auteur: {product.author_name || product.author_id}</span>
                        <span>Categorie: {getProductCategoryLabel(product.category)}</span>
                        {product.subcategory && <span>Sous-categorie: {product.subcategory}</span>}
                        <span>Valeur: {formatCurrency(product.price_mc)}</span>
                        <span>Soumis le: {formatDate(product.created_at)}</span>
                        {product.validated_at && <span>Validé le: {formatDate(product.validated_at)}</span>}
                      </div>
                      {product.rejection_reason && (
                        <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-800">
                          <strong>Raison du rejet:</strong> {product.rejection_reason}
                        </div>
                      )}
                      {product.is_free_for_active_par && (
                        <Badge className="bg-emerald-100 text-emerald-800">Libre PAR actif</Badge>
                      )}
                      {product.institutional_catalogue && (
                        <p className="mt-2 text-xs text-[#5C625F]">
                          Droits: {formatRightsBasis(product.rights_basis)} | Accès: {formatInstitutionalScope(product.institutional_access_scope)}
                        </p>
                      )}
                      {product.institutional_catalogue && (
                        <p className="mt-1 text-xs text-[#5C625F]">Référence: {product.rights_reference || "Non renseignée"}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap lg:shrink-0 lg:justify-end">
                      {product.file_path && (
                        <a
                          href={productsAPI.getPreviewUrl(product.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
                          data-testid={`preview-button-${product.id}`}
                        >
                          <Eye size={16} />
                          Lire le fichier
                        </a>
                      )}
                      {product.institutional_catalogue && product.rights_proof_path && (
                        <a href={getFileUrl(product.rights_proof_path)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-100">
                          <FileText size={16} /> Preuve des droits
                        </a>
                      )}
                      {!product.file_path && (
                        <span className="inline-flex items-center justify-center gap-1 rounded-xl bg-gray-100 px-3 py-2 text-sm text-gray-500">
                          <FileText size={14} />
                          Pas de fichier
                        </span>
                      )}
                      {product.status === "pending" && (
                      <Dialog onOpenChange={(open) => {
                        if (!open) {
                          setSelectedProduct(null);
                          setValidationData({ status: "validated", rejection_reason: "" });
                        }
                      }}>
                        <DialogTrigger asChild>
                          <Button
                            className="bg-[#2A4B46] hover:bg-[#1f3a36] text-white"
                            onClick={() => setSelectedProduct(product)}
                            data-testid={`validate-button-${product.id}`}
                          >
                            valider/Rejeter
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="h-[100dvh] w-screen max-w-none overflow-y-auto rounded-none sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-2xl" data-testid="validation-dialog">
                          <DialogHeader>
                            <DialogTitle className="text-2xl font-bold text-[#2A4B46] font-['Outfit']">
                              valider le produit
                            </DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleValidate} className="space-y-4">
                            <div>
                              <Label>Produit</Label>
                              <p className="text-lg font-semibold text-[#2A4B46]">{selectedProduct?.title}</p>
                            </div>
                            <div>
                              <Label htmlFor="status">Décision</Label>
                              <Select
                                value={validationData.status}
                                onValueChange={(value) =>
                                  setValidationData({ ...validationData, status: value })
                                }
                              >
                                <SelectTrigger className="mt-1" data-testid="validation-status-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="validated">
                                    <div className="flex items-center gap-2">
                                      <CheckCircle size={16} className="text-green-600" />
                                      valider
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="rejected">
                                    <div className="flex items-center gap-2">
                                      <XCircle size={16} className="text-red-600" />
                                      Rejeter
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {validationData.status === "rejected" && (
                              <div>
                                <Label htmlFor="rejection_reason">Raison du rejet</Label>
                                <Textarea
                                  id="rejection_reason"
                                  data-testid="rejection-reason-input"
                                  value={validationData.rejection_reason}
                                  onChange={(e) =>
                                    setValidationData({ ...validationData, rejection_reason: e.target.value })
                                  }
                                  required={validationData.status === "rejected"}
                                  rows={3}
                                  className="mt-1"
                                />
                              </div>
                            )}
                            <Button
                              type="submit"
                              className="w-full bg-[#2A4B46] hover:bg-[#1f3a36] text-white"
                              disabled={validating || !selectedProduct}
                              data-testid="submit-validation-button"
                            >
                              {validating
                                ? "Validation en cours..."
                                : validationData.status === "validated"
                                ? "valider le produit"
                                : "Rejeter le produit"}
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    )}
                      <Button variant="outline" onClick={() => openEdit(product)} data-testid={`edit-product-${product.id}`}>
                        Modifier
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editingProduct} onOpenChange={(open) => !open && setEditingProduct(null)}>
        <DialogContent className="h-[100dvh] w-screen max-w-none overflow-y-auto rounded-none sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#2A4B46] font-['Outfit']">Modifier le produit</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditProduct} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Titre" value={editData.title} onChange={(v) => setEditData({ ...editData, title: v })} />
              <Field label="Nom de l'auteur" value={editData.author_name} onChange={(v) => setEditData({ ...editData, author_name: v })} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Type</Label>
                <Select value={editData.product_type} onValueChange={(v) => setEditData({ ...editData, product_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="texte_simple">Documents textes</SelectItem>
                    <SelectItem value="texte_sonorise">Fichier audio</SelectItem>
                    <SelectItem value="texte_chante">Fichier video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categorie</Label>
                <Select value={editData.category} onValueChange={(v) => setEditData({ ...editData, category: v, subcategory: "" })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    {PRODUCT_CATEGORIES.map((category) => (
                      <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Sous-categorie</Label>
                <Select
                  value={editData.subcategory}
                  onValueChange={(v) => setEditData({ ...editData, subcategory: v })}
                  disabled={!editData.category}
                >
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    {getProductSubcategories(editData.category).map((subcategory) => (
                      <SelectItem key={subcategory} value={subcategory}>{subcategory}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Valeur (MC)" type="number" value={editData.price_mc} onChange={(v) => setEditData({ ...editData, price_mc: v })} />
            </div>
            <TextField label="Description" value={editData.description} onChange={(v) => setEditData({ ...editData, description: v })} />
            <TextField label="Resume" value={editData.summary} onChange={(v) => setEditData({ ...editData, summary: v })} />
            <TextField label="A propos de l'auteur" value={editData.author_about} onChange={(v) => setEditData({ ...editData, author_about: v })} />
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>Image de couverture</Label><Input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setCoverFile(e.target.files?.[0] || null)} className="mt-1" /></div>
              <div><Label>Fichier produit</Label><Input type="file" accept=".pdf,.mp3,.mp4" onChange={(e) => setProductFile(e.target.files?.[0] || null)} className="mt-1" /></div>
            </div>
            <div className="rounded-xl border border-[#E6E4E0] p-3">
              <div className="mb-3 flex items-center gap-2">
                <Checkbox checked={editData.is_free_for_active_par} onCheckedChange={(v) => setEditData({ ...editData, is_free_for_active_par: !!v })} />
                <Label>Rendre libre d'accès aux partenaires PAR actifs</Label>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Debut accès libre" type="datetime-local" value={editData.free_access_start} onChange={(v) => setEditData({ ...editData, free_access_start: v })} />
                <Field label="Fin accès libre" type="datetime-local" value={editData.free_access_end} onChange={(v) => setEditData({ ...editData, free_access_end: v })} />
              </div>
              <p className="mt-2 text-xs text-[#5C625F]">Toute modification d'accès libre PAR demande une approbation four-eyes par un admin FULL.</p>
            </div>
            <TextField label="Notes pour approbation" value={editData.notes} onChange={(v) => setEditData({ ...editData, notes: v })} />
            <Button type="submit" disabled={savingEdit} className="w-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]">
              {savingEdit ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return <div><Label>{label}</Label><Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1" /></div>;
}

function TextField({ label, value, onChange }) {
  return <div><Label>{label}</Label><Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="mt-1" /></div>;
}

function formatRightsBasis(value) {
  return {
    public_domain: "Domaine public",
    creative_commons: "Licence Creative Commons",
    authorized_distribution: "Autorisation de diffusion",
  }[value] || "Non renseigné";
}

function formatInstitutionalScope(value) {
  return {
    public: "Public",
    authenticated: "Utilisateurs connectés",
    active_par: "PAR actifs",
  }[value] || "Non renseigné";
}
